import type { EventDetailMarketDto, MarketOutcomeResultDto } from '@/lib/models';
import { cn } from '@/lib/utils';
import { CollapsibleSection } from './CollapsibleSection';

/**
 * Winner-tile fills cycled per market (Figma 6842:199134 "Outcome", Multiple
 * variant): purple → blue → green → orange. Border is the text colour at 5%.
 */
const TILE_STYLES = [
  { tile: 'bg-input-soft border-brand-grad-deep/5', text: 'text-brand-grad-deep' },
  { tile: 'bg-[#e6efff] border-verified/5', text: 'text-verified' },
  { tile: 'bg-chip-green border-up/5', text: 'text-up' },
  { tile: 'bg-warning-soft border-warning/5', text: 'text-warning' },
] as const;

export interface MarketResultProps {
  /** The event's (closed) markets, carrying `results` + `winningOutcomeId`. */
  markets: EventDetailMarketDto[];
  /** Section title (admin-ordered). */
  title: string;
  /** Initial expanded state. */
  defaultOpen?: boolean;
  /** Expand/collapse analytics. */
  onToggle?: (open: boolean) => void;
}

/**
 * Outcome section for a closed market page: winners only, no probabilities or
 * candidate lists. A single resolved BINARY market renders the flat Yes/No
 * verdict bar (Figma 6682-170224, Green/Red variants — title inline, no
 * collapse); anything else renders the collapsible section with one filled
 * tile per resolved market (Figma 6842:199134, Multiple variant). Markets
 * whose winner isn't derivable yet are skipped; with none resolved the
 * section shows the "Awaiting result" summary.
 */
export function MarketResult({ markets, title, defaultOpen, onToggle }: MarketResultProps) {
  const resolved = markets
    .map((market) => ({ market, winner: winnerOf(market) }))
    .filter((r): r is { market: EventDetailMarketDto; winner: MarketOutcomeResultDto } => r.winner != null);

  if (resolved.length === 0) {
    return <CollapsibleSection title={title} disabled summary={<span className="text-muted">Awaiting result</span>} />;
  }

  // The flat green/red bar renders only when the single verdict IS Yes/No.
  // Gate on the winner's label, not renderKind: a settled market has no active
  // outcomes, so renderKind degrades to BINARY even for a 3-way moneyline —
  // named winners (Argentina, JD Vance, …) always get the tile treatment.
  const verdict = resolved.length === 1 ? resultLabel(resolved[0].winner) : null;
  if (verdict === 'Yes' || verdict === 'No') {
    const won = verdict === 'Yes';
    return (
      <div
        className={cn(
          'flex h-[60px] w-full items-center gap-8 rounded-[12px] border-[1.5px] px-[22px]',
          won ? 'border-up/5 bg-[#e8f6ed]' : 'border-down/5 bg-[#fae7e9]',
        )}
      >
        <span className="text-lg font-medium text-ink">{title}</span>
        <span className={cn('truncate text-[20px] font-semibold', won ? 'text-up' : 'text-down')}>{verdict}</span>
      </div>
    );
  }

  return (
    <CollapsibleSection title={title} defaultOpen={defaultOpen} onToggle={onToggle}>
      <div className="flex flex-col gap-2">
        {resolved.map(({ winner }, i) => {
          const style = TILE_STYLES[i % TILE_STYLES.length];
          return (
            <div
              key={winner.outcomeId}
              className={cn('flex h-[61px] w-full items-center rounded-[12px] border-[1.5px] px-[22px]', style.tile)}
            >
              <span className={cn('truncate text-lg font-semibold', style.text)}>{resultLabel(winner)}</span>
            </div>
          );
        })}
      </div>
    </CollapsibleSection>
  );
}

/** The winning row of a market — by `winningOutcomeId`, else the YES verdict. */
function winnerOf(market: EventDetailMarketDto): MarketOutcomeResultDto | undefined {
  return (
    market.results.find((r) => r.outcomeId === market.winningOutcomeId) ?? market.results.find((r) => r.result === 'YES')
  );
}

/** A winning answer's display name — its label, else its verdict as Yes/No. */
function resultLabel(row: MarketOutcomeResultDto): string {
  return row.label ?? (row.result === 'YES' ? 'Yes' : 'No');
}
